import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * @typedef {object} QuickSize
 * @property {number} id
 * @property {string} label
 * @property {boolean} available
 *
 * @typedef {object} QuickSizeProduct
 * @property {number} id
 * @property {string} title
 * @property {string} image
 * @property {string} optionName
 * @property {boolean} direct
 * @property {QuickSize[]} sizes
 *
 * @typedef {object} QuickSizeConfig
 * @property {string} moneyFormat
 * @property {string} checkoutUrl
 * @property {string} cartUrl
 * @property {string} addUrl
 * @property {string} adding
 * @property {string} add
 * @property {string} checkout
 * @property {string} heading
 * @property {string} note
 * @property {string} subtotal
 * @property {string} soldOut
 * @property {string} error
 * @property {string} close
 */

/**
 * @param {number} cents
 * @param {string} format
 */
function formatMoney(cents, format) {
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const token = format.match(placeholder)?.[1] || 'amount';

  const withDelimiters = (amount, precision, thousands = ',', decimal = '.') => {
    const fixed = (amount / 100).toFixed(precision);
    const [whole, fraction] = fixed.split('.');
    const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };

  /** @type {Record<string, string>} */
  const formats = {
    amount: withDelimiters(cents, 2),
    amount_no_decimals: withDelimiters(cents, 0),
    amount_with_comma_separator: withDelimiters(cents, 2, '.', ','),
    amount_no_decimals_with_comma_separator: withDelimiters(cents, 0, '.', ','),
    amount_with_apostrophe_separator: withDelimiters(cents, 2, "'", '.'),
    amount_no_decimals_with_space_separator: withDelimiters(cents, 0, ' '),
    amount_with_space_separator: withDelimiters(cents, 2, ' ', ','),
    amount_with_period_and_space_separator: withDelimiters(cents, 2, ' ', '.'),
  };

  return format.replace(placeholder, formats[token] || formats.amount);
}

class QuickSizeCard extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const plus = target?.closest('[data-quick-size-plus]');
    const size = target?.closest('[data-quick-size-option]');
    if (!plus && !size) return;

    event.preventDefault();
    event.stopPropagation();

    const dialog = document.querySelector('quick-size-dialog');
    if (!(dialog instanceof QuickSizeDialog)) return;

    const product = this.#product();
    if (!product) return;

    if (plus) {
      if (product.direct) {
        const only = product.sizes.find((entry) => entry.available);
        if (only) dialog.addAndCheckout(only.id, product);
        return;
      }
      dialog.openPicker(product);
      return;
    }

    if (!(size instanceof HTMLButtonElement) || size.disabled) return;
    size.setAttribute('aria-busy', 'true');
    dialog.addAndCheckout(Number(size.dataset.variantId), product).finally(() => {
      size.removeAttribute('aria-busy');
    });
  };

  /** @returns {QuickSizeProduct | null} */
  #product() {
    const script = this.querySelector('script[type="application/json"]');
    if (!script?.textContent) return null;
    return JSON.parse(script.textContent);
  }
}

class QuickSizeDialog extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {QuickSizeConfig | null} */
  #config = null;

  /** @type {QuickSizeProduct | null} */
  #product = null;

  /** @type {number | null} */
  #selectedId = null;

  #requestId = 0;

  connectedCallback() {
    super.connectedCallback();
    const configScript = this.querySelector('[data-quick-size-config]');
    this.#config = configScript?.textContent ? JSON.parse(configScript.textContent) : null;

    this.querySelector('[data-quick-size-close]')?.addEventListener('click', () => {
      this.closeDialog();
    });
    this.querySelector('[data-quick-size-add]')?.addEventListener('click', () => {
      if (this.#selectedId == null || !this.#product) return;
      this.addAndCheckout(this.#selectedId, this.#product);
    });
    this.querySelector('[data-quick-size-size-list]')?.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('[data-variant-id]');
      if (!(button instanceof HTMLButtonElement) || button.disabled) return;
      this.#selectedId = Number(button.dataset.variantId);
      this.querySelectorAll('.quick-size-dialog__size').forEach((sizeButton) => {
        sizeButton.setAttribute('aria-pressed', String(sizeButton === button));
      });
      this.#syncSelectedLabel();
      this.#syncAddButton();
    });
    this.querySelector('[data-quick-size-items]')?.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const control = target?.closest('[data-line-key]');
      if (!(control instanceof HTMLButtonElement) || control.disabled) return;
      const key = control.dataset.lineKey;
      if (!key) return;
      const next = control.dataset.remove != null ? 0 : Number(control.dataset.quantity);
      if (!Number.isFinite(next)) return;
      const limit = this.#stockLimit(control.dataset.variantId);
      if (control.dataset.remove == null && limit != null && next > limit) {
        this.#setNotice('');
        this.#setStatus(limit === 0 ? 'Sold out' : `Only ${limit} in stock`);
        return;
      }
      this.#changeLine(key, next);
    });
    this.querySelector('[data-quick-size-discount-toggle]')?.addEventListener('click', () => {
      const entry = this.querySelector('[data-quick-size-discount-entry]');
      const toggle = this.querySelector('[data-quick-size-discount-toggle]');
      if (!(entry instanceof HTMLElement) || !(toggle instanceof HTMLButtonElement)) return;
      entry.hidden = !entry.hidden;
      toggle.setAttribute('aria-expanded', String(!entry.hidden));
      if (!entry.hidden) entry.querySelector('input')?.focus();
    });
    this.querySelector('[data-quick-size-discount]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      const input = this.querySelector('[data-quick-size-discount] input');
      if (!(input instanceof HTMLInputElement)) return;
      const code = input.value.trim();
      if (!code) return;
      this.#applyDiscount(code).then((applied) => {
        if (applied) input.value = '';
      });
    });
    this.querySelector('[data-quick-size-discount-codes]')?.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('[data-discount-code]');
      if (!(button instanceof HTMLButtonElement)) return;
      const code = button.dataset.discountCode;
      if (code) this.#removeDiscount(code);
    });
  }

  /**
   * @param {QuickSizeProduct} product
   */
  openPicker(product) {
    this.#product = product;
    this.#selectedId = product.sizes.find((size) => size.available)?.id ?? null;
    this.#renderPicker(product);
    this.#showView('picker');
    this.#readCart().catch(() => null);
    this.showDialog();
  }

  /**
   * Reads the current cart, adds the size, then shows every line in the checkout sheet.
   * @param {number} variantId
   * @param {QuickSizeProduct} product
   */
  async addAndCheckout(variantId, product) {
    const config = this.#config;
    if (!config) return;

    const requestId = ++this.#requestId;
    this.#product = product;
    this.#showView('checkout');
    this.#setStatus('');
    this.showDialog();

    try {
      const cartBefore = await this.#readCart();
      if (requestId !== this.#requestId) return;
      this.#assertInStock(variantId, cartBefore);

      this.#setNotice('');
      this.#clearItems();
      const added = await this.#addVariant(variantId);
      const cart = await this.#readCart();
      if (requestId !== this.#requestId) return;

      this.#renderCart(cart);
      this.#setStatus('');
      this.#setNotice('Added to your cart!');
      document.dispatchEvent(
        new CartAddEvent({}, String(variantId), {
          source: 'quick-size',
          itemCount: cart.item_count,
          productId: String(product.id),
          sections: added.sections,
        })
      );
      this.querySelector('[data-quick-size-checkout-link]')?.focus();
    } catch (error) {
      if (requestId !== this.#requestId) return;
      const cart = await this.#readCart().catch(() => null);
      if (cart) this.#renderCart(cart);
      this.#setNotice('');
      const message = error instanceof Error && error.message ? error.message : config.error;
      this.#setStatus(message);
    }
  }

  /**
   * @param {'picker' | 'checkout'} view
   */
  #showView(view) {
    const picker = this.querySelector('[data-quick-size-picker]');
    const checkout = this.querySelector('[data-quick-size-checkout]');
    if (picker instanceof HTMLElement) picker.hidden = view !== 'picker';
    if (checkout instanceof HTMLElement) checkout.hidden = view !== 'checkout';
  }

  /**
   * @param {QuickSizeProduct} product
   */
  #renderPicker(product) {
    const config = this.#config;
    const image = this.querySelector('[data-quick-size-image]');
    const title = this.querySelector('[data-quick-size-title]');
    const list = this.querySelector('[data-quick-size-size-list]');
    if (image instanceof HTMLImageElement) {
      image.src = product.image || '';
      image.alt = product.title;
      image.hidden = !product.image;
    }
    const price = this.querySelector('[data-quick-size-price]');
    if (title) {
      const name = document.createElement('strong');
      name.textContent = product.title;
      title.replaceChildren(name);
    }
    if (price) price.textContent = product.price || '';
    if (list) {
      list.setAttribute('aria-label', product.optionName || 'Size');
      list.replaceChildren();
      for (const size of product.sizes) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'quick-size-dialog__size';
        button.textContent = size.label;
        button.dataset.variantId = String(size.id);
        button.setAttribute('aria-pressed', String(size.id === this.#selectedId));
        if (!size.available) {
          button.disabled = true;
          button.classList.add('quick-size-dialog__size--sold-out');
          button.setAttribute('aria-label', `${size.label}, ${config?.soldOut || 'Sold out'}`);
        }
        list.append(button);
      }
    }
    this.#setPickerStatus('');
    this.#syncSelectedLabel();
    this.#syncAddButton();
  }

  #syncSelectedLabel() {
    const label = this.querySelector('[data-quick-size-selected]');
    const selected = this.#product?.sizes.find((size) => size.id === this.#selectedId);
    if (label) label.textContent = selected?.label || '';
  }

  #syncAddButton() {
    const button = this.querySelector('[data-quick-size-add]');
    if (!(button instanceof HTMLButtonElement) || !this.#config) return;
    const selected = this.#product?.sizes.find((size) => size.id === this.#selectedId && size.available);
    button.disabled = !selected;
    button.textContent = this.#config.add;
  }

  /** @param {string} message */
  #setPickerStatus(message) {
    const status = this.querySelector('[data-quick-size-picker-status]');
    if (!(status instanceof HTMLElement)) return;
    status.textContent = message;
    status.hidden = message === '';
  }

  /**
   * @param {string} message
   * @param {'added' | 'updated' | 'removed'} [tone]
   */
  #setNotice(message, tone = 'added') {
    const banner = this.querySelector('[data-quick-size-added]');
    const text = this.querySelector('[data-quick-size-added-text]');
    const mark = this.querySelector('[data-quick-size-added-mark]');
    if (!(banner instanceof HTMLElement)) return;
    if (!message) {
      banner.hidden = true;
      return;
    }
    if (text) text.textContent = message;
    if (mark) mark.textContent = tone === 'removed' ? '×' : '✓';
    banner.hidden = false;
  }

  /** @param {string} message */
  #setStatus(message) {
    const status = this.querySelector('[data-quick-size-status]');
    if (status) status.textContent = message;
  }

  #clearItems() {
    this.querySelector('[data-quick-size-items]')?.replaceChildren();
    const total = this.querySelector('[data-quick-size-total]');
    if (total) total.textContent = '';
  }

  /**
   * @param {number} variantId
   */
  async #addVariant(variantId) {
    const config = this.#config;
    if (!config) throw new Error('Missing config');

    const body = new FormData();
    body.set('id', String(variantId));
    body.set('quantity', '1');
    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component')]
          .map((node) => (node instanceof HTMLElement ? node.dataset.sectionId : ''))
          .filter(Boolean)
      ),
    ];
    if (sectionIds.length) body.set('sections', sectionIds.join(','));

    const response = await fetch(config.addUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body,
    });
    const payload = await response.json();
    if (!response.ok || payload.status) {
      throw new Error(payload.description || payload.message || config.error);
    }
    return payload;
  }

  /**
   * @param {string} key
   * @param {number} quantity
   */
  async #changeLine(key, quantity) {
    const config = this.#config;
    if (!config?.changeUrl) return;

    const requestId = ++this.#requestId;
    const previous = this.#quantityForKey(key);
    this.#setStatus('');
    try {
      const body = new FormData();
      body.set('id', key);
      body.set('quantity', String(quantity));
      const response = await fetch(config.changeUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const cart = await response.json();
      if (!response.ok || cart.status) {
        throw new Error(cart.description || cart.message || config.error);
      }
      if (requestId !== this.#requestId) return;
      this.#renderCart(cart);
      this.#setStatus('');
      if (quantity <= 0) this.#setNotice('Removed from your cart', 'removed');
      else if (quantity > previous) this.#setNotice('Added to your cart!');
      else this.#setNotice('Updated your cart', 'updated');
      document.dispatchEvent(
        new CartAddEvent({}, key, {
          source: 'quick-size',
          itemCount: cart.item_count,
        })
      );
    } catch (error) {
      if (requestId !== this.#requestId) return;
      this.#setNotice('');
      const message = error instanceof Error && error.message ? error.message : config.error;
      this.#setStatus(message);
    }
  }

  /**
   * @param {string} key
   */
  #quantityForKey(key) {
    const button = this.querySelector(`[data-line-key="${CSS.escape(key)}"]`);
    const amount = button?.parentElement?.querySelector('span');
    const value = Number(amount?.textContent);
    return Number.isFinite(value) ? value : 0;
  }

  /**
   * Tracked stock for a size on this page. Untracked sizes, and sizes set to keep selling, have no cap.
   * @param {number | string | undefined} variantId
   * @returns {number | null}
   */
  #stockLimit(variantId) {
    const id = Number(variantId);
    if (!id) return null;
    for (const script of document.querySelectorAll('quick-size-card script[type="application/json"]')) {
      let product;
      try {
        product = JSON.parse(script.textContent || '');
      } catch {
        continue;
      }
      const size = product.sizes?.find((entry) => entry.id === id);
      if (!size) continue;
      if (!size.tracked || size.policy === 'continue') return null;
      const inventory = Number(size.inventory);
      return Number.isFinite(inventory) ? Math.max(0, inventory) : null;
    }
    return null;
  }

  /**
   * @param {number} variantId
   * @param {{ items?: Array<{ variant_id?: number, quantity: number }> }} cart
   */
  #assertInStock(variantId, cart) {
    const limit = this.#stockLimit(variantId);
    if (limit == null) return;
    const inCart = cart.items?.find((item) => item.variant_id === variantId)?.quantity || 0;
    if (inCart >= limit) {
      throw new Error(limit === 0 ? 'Sold out' : `Only ${limit} in stock`);
    }
  }

  async #readCart() {
    const config = this.#config;
    if (!config) throw new Error('Missing config');
    const response = await fetch(config.cartUrl, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(config.error);
    return response.json();
  }

  /**
   * @param {{ item_count: number, total_price: number, items: Array<{ key?: string, product_title?: string, title?: string, variant_title?: string | null, quantity: number, final_price: number, final_line_price: number, image?: string | null, line_level_discount_allocations?: Array<{ amount: number, discount_application?: { title?: string } }> }>, cart_level_discount_applications?: Array<{ title?: string, total_allocated_amount?: number }> }} cart
   */
  #renderCart(cart) {
    const config = this.#config;
    const list = this.querySelector('[data-quick-size-items]');
    if (!config || !list) return;

    const count = this.querySelector('[data-quick-size-count]');
    if (count) {
      const noun = cart.item_count === 1 ? 'item' : 'items';
      count.textContent = `You have ${cart.item_count} ${noun} in your shopping bag`;
    }

    list.replaceChildren();
    for (const item of cart.items) {
      const row = document.createElement('li');
      row.className = 'quick-size-dialog__item';

      const imageSrc = item.image?.startsWith('//') ? `https:${item.image}` : item.image;
      if (imageSrc?.startsWith('http')) {
        const image = document.createElement('img');
        image.alt = '';
        image.width = 72;
        image.height = 90;
        image.src = imageSrc.includes('?') ? `${imageSrc}&width=180` : `${imageSrc}?width=180`;
        row.append(image);
      } else {
        row.append(document.createElement('span'));
      }

      const copy = document.createElement('div');
      const title = document.createElement('p');
      title.className = 'quick-size-dialog__item-title';
      const name = document.createElement('strong');
      name.textContent = item.product_title || item.title || '';
      title.append(name);
      copy.append(title);

      if (item.variant_title && item.variant_title !== 'Default Title') {
        const size = document.createElement('p');
        size.className = 'quick-size-dialog__item-meta';
        size.textContent = item.variant_title;
        copy.append(size);
      }

      const price = document.createElement('p');
      price.className = 'quick-size-dialog__item-meta';
      price.textContent = formatMoney(item.final_price, config.moneyFormat);
      copy.append(price);

      for (const discount of item.line_level_discount_allocations || []) {
        const line = document.createElement('p');
        line.className = 'quick-size-dialog__item-meta';
        const name = discount.discount_application?.title || 'Discount';
        line.textContent = `${name} −${formatMoney(discount.amount, config.moneyFormat)}`;
        copy.append(line);
      }

      const qty = document.createElement('div');
      qty.className = 'quick-size-dialog__qty';
      const stepper = document.createElement('div');
      stepper.className = 'quick-size-dialog__stepper';
      const key = item.key || '';

      const minus = document.createElement('button');
      minus.type = 'button';
      minus.dataset.lineKey = key;
      minus.dataset.quantity = String(Math.max(0, item.quantity - 1));
      minus.setAttribute('aria-label', 'Decrease quantity');
      minus.append(this.#icon('minus'));

      const amount = document.createElement('span');
      amount.textContent = String(item.quantity);

      const plus = document.createElement('button');
      plus.type = 'button';
      plus.dataset.lineKey = key;
      plus.dataset.variantId = String(item.variant_id || '');
      plus.dataset.quantity = String(item.quantity + 1);
      const limit = this.#stockLimit(item.variant_id);
      if (limit != null && item.quantity >= limit) {
        plus.disabled = true;
        plus.setAttribute('aria-label', limit === 0 ? 'Sold out' : `Only ${limit} in stock`);
      } else {
        plus.setAttribute('aria-label', 'Increase quantity');
      }
      plus.append(this.#icon('plus'));

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'quick-size-dialog__remove';
      remove.dataset.lineKey = key;
      remove.dataset.remove = '';
      remove.setAttribute('aria-label', 'Remove');
      remove.append(this.#icon('remove'));

      stepper.append(minus, amount, plus);
      qty.append(stepper, remove);
      copy.append(qty);
      row.append(copy);
      list.append(row);
    }

    for (const discount of cart.cart_level_discount_applications || []) {
      const row = document.createElement('li');
      row.className = 'quick-size-dialog__item-meta';
      row.textContent = `${discount.title || 'Discount'} −${formatMoney(discount.total_allocated_amount || 0, config.moneyFormat)}`;
      list.append(row);
    }

    const total = this.querySelector('[data-quick-size-total]');
    if (total) total.textContent = formatMoney(cart.total_price, config.moneyFormat);
    this.#renderDiscountCodes(cart.discount_codes || []);
  }

  /**
   * @param {string} name
   */
  #icon(name) {
    const template = this.querySelector(`[data-quick-size-icon="${name}"]`);
    const wrap = document.createElement('span');
    wrap.className = 'svg-wrapper';
    wrap.innerHTML = template instanceof HTMLTemplateElement ? template.innerHTML : '';
    return wrap;
  }

  /**
   * @param {Array<{ code?: string, applicable?: boolean }>} codes
   */
  #renderDiscountCodes(codes) {
    const list = this.querySelector('[data-quick-size-discount-codes]');
    if (!list) return;
    list.replaceChildren();
    for (const discount of codes) {
      if (!discount.code || discount.applicable === false) continue;
      const item = document.createElement('li');
      item.className = 'quick-size-dialog__discount-code';
      item.textContent = discount.code;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.discountCode = discount.code;
      remove.setAttribute('aria-label', `Remove discount ${discount.code}`);
      remove.textContent = '×';
      item.append(remove);
      list.append(item);
    }
  }

  /** @param {string} message */
  #setDiscountError(message) {
    const error = this.querySelector('[data-quick-size-discount-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = message === '';
  }

  /**
   * @param {string[]} codes
   */
  async #updateDiscounts(codes) {
    const config = this.#config;
    if (!config?.updateUrl) return null;
    const response = await fetch(config.updateUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ discount: codes.join(',') }),
    });
    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || cart.message || config.error);
    return cart;
  }

  /**
   * @param {string} code
   */
  async #applyDiscount(code) {
    const config = this.#config;
    if (!config) return false;
    this.#setDiscountError('');
    try {
      const current = await this.#readCart();
      const existing = (current.discount_codes || []).map((discount) => discount.code).filter(Boolean);
      if (existing.some((saved) => saved.toLowerCase() === code.toLowerCase())) {
        this.#renderCart(current);
        return true;
      }
      const cart = await this.#updateDiscounts([...existing, code]);
      const match = cart?.discount_codes?.find((discount) => discount.code?.toLowerCase() === code.toLowerCase());
      if (!match || match.applicable === false) {
        this.#setDiscountError('Enter a valid discount code');
        if (cart) this.#renderCart(cart);
        return false;
      }
      this.#renderCart(cart);
      this.#setNotice('Discount applied', 'updated');
      return true;
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : config.error;
      this.#setDiscountError(message);
      return false;
    }
  }

  /**
   * @param {string} code
   */
  async #removeDiscount(code) {
    const config = this.#config;
    if (!config) return;
    try {
      const current = await this.#readCart();
      const remaining = (current.discount_codes || [])
        .map((discount) => discount.code)
        .filter((saved) => saved && saved.toLowerCase() !== code.toLowerCase());
      const cart = await this.#updateDiscounts(remaining);
      if (cart) this.#renderCart(cart);
      this.#setNotice('Discount removed', 'removed');
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : config.error;
      this.#setDiscountError(message);
    }
  }
}

if (!customElements.get('quick-size-card')) {
  customElements.define('quick-size-card', QuickSizeCard);
}

if (!customElements.get('quick-size-dialog')) {
  customElements.define('quick-size-dialog', QuickSizeDialog);
}
